import * as React from 'react'
import type {
  IrrigationRepairRow,
  ProposalDocumentDto,
  TreeScheduleRow,
} from '@lib/dto/proposal-document.dto'
import {
  isOfferedPlan,
  type OfferedProposalPlan,
  type ProposalPlan,
} from '@lib/enums/proposal-plan.enum'
import type { TreeWorkChoice } from '@lib/enums/tree-work-choice.enum'
import {
  EMPTY_SELECTION,
  priceSelection,
  repairDropped,
  selectionDropReason,
  treeChoiceFor,
  type ProposalSelection,
  type SelectionOutcome,
} from '@lib/helpers/proposal-selection'
import {
  NO_ADJUSTMENTS,
  recommendedPlan,
  describeAdjustments,
  effectiveSelection,
  planFigures,
  presetSelection,
  withoutChange,
  type AdjustmentChange,
  type Adjustments,
  type PlanFigures,
} from '@lib/helpers/proposal-plans'
import type { TreeStatus } from '@/proposal-document/canopy'
import { isTreeIncluded } from '@/proposal-document/choices'

/**
 * What a reader has chosen: a plan, and their own changes on top. Held as two
 * things rather than one flat selection, so each change can be named and
 * undone and a plan can be switched without losing them.
 */
export interface PlanState {
  plan: OfferedProposalPlan
  adjustments: Adjustments
}

export const INITIAL_PLAN_STATE: PlanState = {
  plan: 'ALL',
  adjustments: NO_ADJUSTMENTS,
}

/**
 * A version as a link brings it back: any plan a proposal has ever offered,
 * since a link shared before a plan was withdrawn still opens.
 */
export interface SharedVersion {
  plan: ProposalPlan
  adjustments: Adjustments
}

/** Everything a page needs to show and change what is being bought. */
export interface Choosing {
  document: ProposalDocumentDto
  state: PlanState
  /** The plan with the changes applied: what is actually priced. */
  selection: ProposalSelection
  outcome: SelectionOutcome
  figures: PlanFigures[]
  changes: AdjustmentChange[]
  /** True once accepted: the choices stay visible but stop changing. */
  isLocked: boolean
  /**
   * Whether the arborist marked some work optional, which splits "everything"
   * into the recommendation (CORE) and everything with the extras (ALL).
   */
  hasOptional: boolean
  setPlan: (plan: OfferedProposalPlan) => void
  adjust: (next: Adjustments) => void
  undo: (change: AdjustmentChange) => void
  reset: () => void
  treeStatus: (site: string, row: TreeScheduleRow) => TreeStatus
  /** Whether an irrigation repair is in or out. */
  repairStatus: (site: string, repair: IrrigationRepairRow) => TreeStatus
  /** Why a tree cannot be put back on its own, or null when it can. */
  groupReason: (site: string, row: TreeScheduleRow) => string | null
  setTree: (site: string, row: TreeScheduleRow, want: TreeWant) => void
  /** The prune-or-remove answer in force for a tree. */
  workChoice: (site: string, row: TreeScheduleRow) => TreeWorkChoice
  /**
   * What left a repair out along with others (its property, all the
   * irrigation, its kind, zone or area), or null when it can be chosen on
   * its own.
   */
  repairGroupReason: (site: string, repair: IrrigationRepairRow) => string | null
  setRepair: (site: string, repair: IrrigationRepairRow, want: 'in' | 'out') => void
}

/** What a reader can ask for one tree. */
export type TreeWant = 'in' | 'out' | 'prune' | 'remove'

function sameTree(
  a: { site: string; label: string },
  b: { site: string; label: string },
): boolean {
  return a.site === b.site && a.label === b.label
}

function sameRepair(
  a: { site: string; key: string },
  b: { site: string; key: string },
): boolean {
  return a.site === b.site && a.key === b.key
}

/**
 * The reader's plan and changes, and everything derived from them.
 *
 * Pure derivation over a small piece of state: the selection, the price and
 * the list of changes are recomputed from it on every change, so they cannot
 * drift from each other. The pricing is the shared helper the acceptance API
 * also runs, so what this shows is what gets recorded.
 */
export function useChoosing(
  document: ProposalDocumentDto,
  options: { initial?: SharedVersion | undefined; isLocked?: boolean } = {},
): Choosing {
  // A link to a plan no longer offered opens on the recommendation, with the
  // changes it carried.
  const [state, setState] = React.useState<PlanState>(() => {
    const { initial } = options
    return {
      plan:
        initial !== undefined && isOfferedPlan(initial.plan)
          ? initial.plan
          : recommendedPlan(document),
      adjustments: initial?.adjustments ?? NO_ADJUSTMENTS,
    }
  })
  const isLocked = options.isLocked ?? false

  const derived = React.useMemo(() => {
    const preset = presetSelection(document, state.plan)
    const selection = effectiveSelection(document, state.plan, state.adjustments)
    const outcome = priceSelection(document, selection)
    const changes = describeAdjustments(document, state.plan, state.adjustments)
    const figures = planFigures(document)
    return { preset, selection, outcome, changes, figures }
  }, [document, state])

  const { selection } = derived

  const treeStatus = React.useCallback(
    (site: string, row: TreeScheduleRow): TreeStatus =>
      isTreeIncluded(site, row, selection) ? 'in' : 'out',
    [selection],
  )

  const repairStatus = React.useCallback(
    (site: string, repair: IrrigationRepairRow): TreeStatus =>
      repairDropped(document, site, repair, selection) ? 'out' : 'in',
    [document, selection],
  )

  const groupReason = React.useCallback(
    (site: string, row: TreeScheduleRow): string | null => {
      const reason = selectionDropReason(site, row, selection)
      if (reason === 'site') return site
      if (reason === 'area' || reason === 'areaName') return row.areaName ?? 'Its area'
      if (reason === 'species') return row.species?.name ?? 'Unidentified trees'
      return null
    },
    [selection],
  )

  const repairGroupReason = React.useCallback(
    (site: string, repair: IrrigationRepairRow): string | null => {
      if (selection.excludedSites.includes(site)) return site
      if (selection.excludedIrrigation) return 'all the irrigation repairs'
      if (selection.excludedIrrigationKinds.includes(repair.kind)) return repair.kind
      if (
        repair.zone !== null &&
        selection.excludedZones.some(
          (zone) => zone.site === site && zone.zone === repair.zone,
        )
      ) {
        return repair.zone
      }
      // Still out with its own choice taken away: only its area is left.
      const apart = {
        ...selection,
        excludedRepairs: selection.excludedRepairs.filter(
          (entry) => !sameRepair(entry, { site, key: repair.key }),
        ),
      }
      return repairDropped(document, site, repair, apart)
        ? (repair.areaName ?? 'its area')
        : null
    },
    [document, selection],
  )

  const workChoice = React.useCallback(
    (site: string, row: TreeScheduleRow): TreeWorkChoice =>
      treeChoiceFor(site, row, selection),
    [selection],
  )

  const update = (next: (current: PlanState) => PlanState): void => {
    if (isLocked) return
    setState(next)
  }

  const setTree = (site: string, row: TreeScheduleRow, want: TreeWant): void => {
    update((current) => {
      const tree = { site, label: row.label }
      const a = current.adjustments
      const others = {
        excludedTrees: a.excludedTrees.filter((entry) => !sameTree(entry, tree)),
        includedTrees: a.includedTrees.filter((entry) => !sameTree(entry, tree)),
        treeChoices: a.treeChoices.filter((entry) => !sameTree(entry, tree)),
      }
      const presetOut = presetSelection(document, current.plan).excludedTrees.some(
        (entry) => sameTree(entry, tree),
      )
      const defaultChoice = treeChoiceFor(site, row, EMPTY_SELECTION)

      const choice = (value: TreeWorkChoice): Adjustments['treeChoices'] =>
        value === defaultChoice
          ? others.treeChoices
          : [...others.treeChoices, { site, label: row.label, choice: value }]

      let next: Adjustments
      switch (want) {
        case 'out':
          next = {
            ...a,
            ...others,
            // Out by the plan already: nothing to record.
            excludedTrees: presetOut
              ? others.excludedTrees
              : [...others.excludedTrees, tree],
            treeChoices: others.treeChoices,
          }
          break
        case 'in':
          next = {
            ...a,
            ...others,
            includedTrees: presetOut
              ? [...others.includedTrees, tree]
              : others.includedTrees,
            treeChoices: choice(defaultChoice),
          }
          break
        case 'prune':
        case 'remove':
          next = {
            ...a,
            ...others,
            includedTrees: presetOut
              ? [...others.includedTrees, tree]
              : others.includedTrees,
            treeChoices: choice(want === 'prune' ? 'PRUNE' : 'REMOVE'),
          }
          break
      }
      return { ...current, adjustments: next }
    })
  }

  /** One repair in or out, recorded as a tree's choice is. */
  const setRepair = (
    site: string,
    repair: IrrigationRepairRow,
    want: 'in' | 'out',
  ): void => {
    update((current) => {
      const entry = { site, key: repair.key }
      const a = current.adjustments
      const excludedRepairs = a.excludedRepairs.filter(
        (other) => !sameRepair(other, entry),
      )
      const includedRepairs = a.includedRepairs.filter(
        (other) => !sameRepair(other, entry),
      )
      const presetOut = presetSelection(document, current.plan).excludedRepairs.some(
        (other) => sameRepair(other, entry),
      )
      const next: Adjustments =
        want === 'out'
          ? {
              ...a,
              includedRepairs,
              // Out by the plan already: nothing to record.
              excludedRepairs: presetOut
                ? excludedRepairs
                : [...excludedRepairs, entry],
            }
          : {
              ...a,
              excludedRepairs,
              includedRepairs: presetOut
                ? [...includedRepairs, entry]
                : includedRepairs,
            }
      return { ...current, adjustments: next }
    })
  }

  return {
    document,
    state,
    selection,
    outcome: derived.outcome,
    figures: derived.figures,
    changes: derived.changes,
    isLocked,
    hasOptional: derived.figures.some(
      (figure) => figure.plan === 'CORE' && figure.isAvailable,
    ),
    setPlan: (plan) => {
      update((current) => ({ ...current, plan }))
    },
    adjust: (next) => {
      update((current) => ({ ...current, adjustments: next }))
    },
    undo: (change) => {
      update((current) => ({
        ...current,
        adjustments: withoutChange(current.adjustments, change),
      }))
    },
    reset: () => {
      update((current) => ({ ...current, adjustments: NO_ADJUSTMENTS }))
    },
    treeStatus,
    repairStatus,
    groupReason,
    setTree,
    workChoice,
    repairGroupReason,
    setRepair,
  }
}
